'use client'

import { Button } from '@/components/ui/button'
import { SIGNATURE_SURFACE_CLASS } from '@/constant/signature-surface'
import { cn } from '@/lib/utils'
import { Trash2, Upload } from 'lucide-react'
import { useRef, useState } from 'react'
import { toast } from 'sonner'

/** Matches the server-side decoded byte cap. */
const MAX_BYTES = 512 * 1024

const ACCEPTED = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp']

export interface SignatureUploadProps {
  value: string | null
  onChange: (dataUrl: string | null) => void
  disabled?: boolean
  className?: string
}

/**
 * Upload an image of a handwritten signature.
 *
 * SVG is not accepted - it can carry script, and the server rejects it anyway.
 * Validation here is a courtesy to the user; the server revalidates and re-encodes.
 */
const SignatureUpload = ({ value, onChange, disabled = false, className }: SignatureUploadProps) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [fileName, setFileName] = useState<string | null>(null)

  const handleFile = (file: File | undefined) => {
    if (!file) return

    if (!ACCEPTED.includes(file.type)) {
      toast.error('Please upload a PNG, JPG or WebP image')
      return
    }

    if (file.size > MAX_BYTES) {
      toast.error(`The image must be smaller than ${Math.round(MAX_BYTES / 1024)}KB`)
      return
    }

    const reader = new FileReader()
    reader.onload = () => {
      onChange(typeof reader.result === 'string' ? reader.result : null)
      setFileName(file.name)
    }
    reader.onerror = () => toast.error('Could not read that image')
    reader.readAsDataURL(file)
  }

  const clear = () => {
    onChange(null)
    setFileName(null)
    if (inputRef.current) inputRef.current.value = ''
  }

  return (
    <div className={cn('space-y-3', className)}>
      <input
        ref={inputRef}
        type='file'
        accept={ACCEPTED.join(',')}
        className='hidden'
        disabled={disabled}
        onChange={(event) => handleFile(event.target.files?.[0])}
      />

      {value ? (
        <div className='space-y-2'>
          <div
            className={cn(
              'flex items-center justify-center rounded-md border border-dashed p-4',
              SIGNATURE_SURFACE_CLASS
            )}
          >
            <img src={value} alt='Uploaded signature' className='max-h-[120px] max-w-full object-contain' />
          </div>
          <div className='flex items-center justify-between gap-2'>
            <span className='truncate text-xs text-muted-foreground'>{fileName ?? 'Uploaded signature'}</span>
            <Button type='button' variant='destructive' size='sm' onClick={clear} disabled={disabled}>
              <Trash2 className='h-3.5 w-3.5' />
              Remove
            </Button>
          </div>
        </div>
      ) : (
        <button
          type='button'
          disabled={disabled}
          onClick={() => inputRef.current?.click()}
          className={cn(
            'flex w-full flex-col items-center justify-center gap-2 rounded-md border border-dashed bg-background p-8 transition-colors hover:bg-muted/40',
            disabled && 'pointer-events-none opacity-60'
          )}
        >
          <Upload className='h-5 w-5 text-muted-foreground' />
          <span className='text-sm text-muted-foreground'>Upload an image of your signature</span>
          <span className='text-xs text-muted-foreground/70'>
            PNG, JPG or WebP, up to {Math.round(MAX_BYTES / 1024)}KB
          </span>
        </button>
      )}
    </div>
  )
}

export default SignatureUpload
